import React, { Fragment, useEffect, useState } from 'react'
import injectStyles from 'react-jss'
import moment from 'moment'

import { styles } from './styles'
import StyledBreadcrumbs from '../../../Components/StyledBreadcrumbs'
import { PATHS } from '../../../Config/Routing/paths'
import { PageTitle } from '../../../Components/PageTitle'
import { openLinkInNewTab } from '../../Country/Page/Pipeline/PipelineView/utils'
import Input from '../../../Components/Inputs/Input'
import LoadingIndicator from '../../../Components/LoadingIndicator/LoadingIndicator'
import { authorization } from '../../../Module/Auth/Auth'
import { apiXero } from '../../../Module/API/Xero'
import Select from '../../../Components/Inputs/Select'
import { ActionMenu } from '../../../Components/ActionMenu'
import { SettingsIcon } from '../../../Assets/Images/icons/SettingsIcon'
import XeroConnected from '../../../Components/XeroConnected'

const SEARCH_TYPES = {
  CLIENT: 'CLIENT',
  TRACKING_CATEGORY: 'TRACKING_CATEGORY',
  ITEM_CODE: 'ITEM_CODE',
}
// Function to extract query parameters from the URL
const getQueryParameters = url => {
  const queryStart = url.indexOf('?') + 1
  const queryEnd = url.indexOf('#') !== -1 ? url.indexOf('#') : url.length
  const query = url.slice(queryStart, queryEnd)
  const pairs = query.split('&')
  const params = {}

  pairs.forEach(pair => {
    const [key, value] = pair.split('=')
    params[key] = decodeURIComponent(value)
  })

  return params
}

const formatDate = date => moment(date).format('DD MMM, YYYY')

const XeroConnection = ({ classes }) => {
  const [searchContact, setSearchContact] = useState('')
  const [searchTracking, setSearchTracking] = useState('')
  const [searchItemCodes, setSearchItemCodes] = useState('')

  const [contacts, setContacts] = useState([])
  const [trackingCategories, setTrackingCategories] = useState([])
  const [itemCodes, setItemCodes] = useState([])
  const [invoices, setInvoices] = useState([])
  const [filteredContact, setFilteredContacts] = useState([])
  const [filteredTrackingCategories, setFilteredTrackingCategories] = useState(
    [],
  )
  const [filteredItemCodes, setFilteredItemCodes] = useState([])

  const [tenant, setTenant] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [refetch, setRefetch] = useState(true)
  const [connectedTenants, setConnectedTenants] = useState([])
  const [info, setInfo] = useState('Loading')
  const [contactIdForSearchInvoices, setContactIdForSearchInvoices] = useState(
    null,
  )
  const [infoFetchingInvoices, setInfoFetchingInvoices] = useState(
    'Select contact to get his invoices',
  )

  const currentTenantId = authorization.getXeroTenantId()

  useEffect(() => {
    if (refetch) {
      const queryParams = getQueryParameters(window.location.href)
      const xeroAccessToken = queryParams.accessToken
      const xeroRefreshToken = queryParams.refreshToken

      authorization.setXeroTokens({ xeroAccessToken, xeroRefreshToken })

      const cleanedUrl = window.location.origin + window.location.pathname
      window.history.replaceState({}, document.title, cleanedUrl)
      getAllData()
    }
  }, [refetch])

  const getAllData = async () => {
    setIsLoading(true)
    setInfo('Fetching connected entities')
    try {
      const tenants = await apiXero.getXeroConnectedTenants()

      if (tenants.length) {
        const tenantId = tenants[0].tenantId
        if (tenantId) {
          if (!currentTenantId) {
            authorization.setXeroTenantId(tenantId)
          }
        }
        setConnectedTenants(tenants)

        await getXeroTenant()
      } else {
        authorization.setXeroTenantId('')
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log({ err })
    } finally {
      setRefetch(false)
      setIsLoading(false)
    }
  }

  const handleRefetch = () => {
    setIsLoading(true)
    setTenant(null)
    setFilteredContacts([])
    setFilteredTrackingCategories([])
    setFilteredItemCodes([])
    setInvoices([])
    setContacts([])
    setRefetch(true)
  }

  const getXeroTenant = async () => {
    setInfo('Fetching entity data')
    const tenant = await apiXero.getXeroTenant({
      includeAvailableAccount: false,
      includeCurrencies: true,
    })
    if (tenant && tenant.isAuthorized && tenant.isConnected) {
      setTenant(tenant)
      const contacts = await apiXero.getXeroContacts({
        tenantId: tenant.tenantId,
      })
      if (contacts.length) {
        setContacts(contacts)
        setFilteredContacts(contacts)
      }

      const trackingCategories = await apiXero.getXeroTrackingCategories({
        tenantId: tenant.tenantId,
      })
      if (trackingCategories.length) {
        setTrackingCategories(trackingCategories)
        setFilteredTrackingCategories(trackingCategories)
      }
      const itemCodes = await apiXero.getXeroItemCodes({
        tenantId: tenant.tenantId,
      })
      if (itemCodes.length) {
        setItemCodes(itemCodes)
        setFilteredItemCodes(itemCodes)
      }

      const last100Invoices = await apiXero.getXeroInvoices({
        tenantId: tenant.tenantId,
      })
      if (last100Invoices.length) {
        setInvoices(last100Invoices)
      }
    }
  }

  const getActionOptions = () => {
    let options = [
      {
        id: 1,
        name: 'Add new connection',
        onClick: () => {
          const currentURL = window.location.href
          window.location.href = `https://api.oppizi.com/start-xero-auth?redirectUrl=${encodeURIComponent(
            currentURL,
          )}`
        },
      },
      {
        id: 1,
        name: 'Go to Xero hompage',
        onClick: () => {
          openLinkInNewTab('https://login.xero.com/identity/user/login')
        },
      },
    ]

    if (currentTenantId) {
      options.push({
        id: 3,
        name: 'Disconnect',
        onClick: async () => {
          const currentTenant = connectedTenants.find(
            item => item.tenantId === currentTenantId,
          )
          if (currentTenant) {
            setInfo(`Disconnecting from entity: ${currentTenant.tenantName}`)
            setIsLoading(true)

            await apiXero.disconnectXero(currentTenant.id)
            authorization.setXeroTenantId('')
            handleRefetch()
          }
        },
        style: {
          color: '#004360',
        },
      })
    }

    return options
  }

  const actionOptions = getActionOptions()

  const handleFilter = (value, type) => {
    if (type === SEARCH_TYPES.CLIENT) {
      setSearchContact(value)
      setFilteredContacts(
        tenant
          ? contacts.filter(item =>
              item.name.toLowerCase().includes(value.toLowerCase()),
            )
          : [],
      )
    }

    if (type === SEARCH_TYPES.TRACKING_CATEGORY) {
      setSearchTracking(value)
      setFilteredTrackingCategories(
        tenant
          ? trackingCategories.filter(item =>
              item.name.toLowerCase().includes(value.toLowerCase()),
            )
          : [],
      )
    }
    if (type === SEARCH_TYPES.ITEM_CODE) {
      setSearchItemCodes(value)
      setFilteredItemCodes(
        tenant
          ? itemCodes.filter(
              item =>
                (item.code &&
                  item.code.toLowerCase().includes(value.toLowerCase())) ||
                (item.name &&
                  item.name.toLowerCase().includes(value.toLowerCase())),
            )
          : [],
      )
    }
  }

  const handleSelectContact = async value => {
    if (value === 'None') {
      setInfoFetchingInvoices('Loading...')
      setContactIdForSearchInvoices(null)
      const last100Invoices = await apiXero.getXeroInvoices({
        tenantId: tenant.tenantId,
        contactID: null,
      })
      setInvoices(last100Invoices)

      setInfoFetchingInvoices('Select a contact to get all invoices')
    } else {
      setInfoFetchingInvoices('Loading...')

      setContactIdForSearchInvoices(value)

      const contactInvoices = await apiXero.getXeroInvoices({
        tenantId: tenant.tenantId,
        contactID: value,
      })

      setInvoices(contactInvoices)
      setInfoFetchingInvoices('Select a contact to get all invoices')
    }
  }

  return (
    <div className={classes.wrapper}>
      <StyledBreadcrumbs
        icon={<SettingsIcon />}
        breadcrumbs={[
          {
            label: 'Settings',
            link: `${PATHS.main.dashboard}${PATHS.dashboard.settings}`,
          },
          {
            label: 'Xero',
          },
        ]}
      />
      <PageTitle className={classes.title}>Xero</PageTitle>
      {isLoading || refetch ? (
        <LoadingIndicator style={{ height: '50vh' }} label={info} />
      ) : (
        <Fragment>
          <div className={classes.formColumn}>
            <div className={classes.formWrapperColumn}>
              <div className={classes.text}>
                Country code: <span>{tenant ? tenant.countryCode : ' - '}</span>
              </div>
              <XeroConnected isConnected={tenant} />
            </div>
            <div className={classes.formWrapperColumn}>
              <div className={classes.text}>
                Name: <span>{tenant ? tenant.name : ' - '}</span>
              </div>
            </div>
            <div className={classes.formWrapperColumn}>
              <div className={classes.text}>
                Multi-currency:{' '}
                <span>
                  {!tenant
                    ? ' - '
                    : tenant.availableCurrencies.length > 1
                    ? tenant.availableCurrencies.join(', ')
                    : 'No'}
                </span>
              </div>
              <div className={classes.text}>
                Base-currency:{' '}
                <span>{tenant ? tenant.baseCurrency : ' - '}</span>
              </div>
            </div>

            <div style={{ width: '100%', maxWidth: '250px' }}>
              {currentTenantId && connectedTenants.length ? (
                <Select
                  label="Entity"
                  value={currentTenantId}
                  onChange={value => {
                    authorization.setXeroTenantId(value)
                    handleRefetch()
                  }}
                  options={connectedTenants.map(item => ({
                    value: item.tenantId,
                    name: item.tenantName,
                  }))}
                />
              ) : null}
            </div>
            <div className={classes.formWrapperColumn}>
              <ActionMenu options={actionOptions} />
            </div>
          </div>

          <div className={classes.content}>
            <div className={classes.cardRow}>
              <div className={classes.card}>
                <div className={classes.cardForm}>
                  <div className={classes.cardTitle}>Clients</div>
                  <div>
                    <Input
                      placeholder="Type client here"
                      disableError={true}
                      label=""
                      value={searchContact}
                      onChange={value =>
                        handleFilter(value, SEARCH_TYPES.CLIENT)
                      }
                      className={classes.cardSearch}
                    />
                  </div>
                </div>
                <div className={classes.table}>
                  {filteredContact.map(contact => (
                    <div key={contact.name} className={classes.tableRow}>
                      <div className={classes.rowItemGeneral}>
                        {contact.name}
                      </div>
                      <div className={classes.rowItemMain}>
                        {contact.firstName} {contact.lastName}
                      </div>
                      <div className={classes.rowItemMain}>
                        {contact.emailAddress}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={classes.card}>
                <div className={classes.cardForm}>
                  <div className={classes.cardTitle}>Tracking Categories</div>
                  <div className={classes.cardSearch}>
                    <Input
                      placeholder="Type Category here"
                      disableError={true}
                      label=""
                      value={searchTracking}
                      onChange={value =>
                        handleFilter(value, SEARCH_TYPES.TRACKING_CATEGORY)
                      }
                      className={classes.cardSearch}
                    />
                  </div>
                </div>
                <div className={classes.table}>
                  {filteredTrackingCategories.map(tracking => (
                    <div key={tracking.name} className={classes.tableRow}>
                      <div className={classes.rowItemGeneral}>
                        {tracking.name}
                      </div>
                      <div className={classes.rowItemMain}>
                        {tracking.trackingCategoryName}
                      </div>
                      <div className={classes.rowItemMain}>
                        {tracking.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className={classes.cardRow}>
              <div className={classes.card}>
                <div className={classes.cardForm}>
                  <div className={classes.cardTitle}>Item Codes</div>
                  <div className={classes.cardSearch}>
                    <Input
                      placeholder="Type Item Code here"
                      disableError={true}
                      label=""
                      value={searchItemCodes}
                      onChange={value =>
                        handleFilter(value, SEARCH_TYPES.ITEM_CODE)
                      }
                      className={classes.cardSearch}
                    />
                  </div>
                </div>
                <div className={classes.table}>
                  {filteredItemCodes.map(itemCode => (
                    <div key={itemCode.code} className={classes.tableRow}>
                      <div className={classes.rowItemGeneral}>
                        {itemCode.code}
                      </div>
                      <div className={classes.rowItemMain}>{itemCode.name}</div>
                      {/* <div className={classes.rowItemDate}> */}
                      {/* {formatDate(itemCode.updatedAt)} */}
                      {/* </div> */}
                    </div>
                  ))}
                </div>
              </div>
              <div className={classes.card}>
                <div className={classes.cardForm}>
                  <div className={classes.cardTitle}>Invoices</div>
                  {contacts.length ? (
                    <div className={classes.cardSelect}>
                      <span>{infoFetchingInvoices}</span>
                      <div>
                        <Select
                          value={
                            contactIdForSearchInvoices
                              ? contactIdForSearchInvoices
                              : 'None'
                          }
                          onChange={handleSelectContact}
                          options={[
                            { value: 'None', name: 'Last 100 invoices' },
                            ...contacts.map(item => ({
                              value: item.id,
                              name: item.name,
                            })),
                          ]}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className={classes.table}>
                  {invoices.map((invoice, index) => (
                    <div
                      key={invoice.invoiceNumber || index}
                      className={classes.tableRow}
                    >
                      <div className={classes.rowItemGeneral}>
                        {invoice.invoiceNumber}
                      </div>
                      <div className={classes.rowItemMain}>
                        {invoice.contactName}
                      </div>
                      <div className={classes.rowItemMain}>
                        {invoice.status}
                      </div>
                      <div className={classes.rowItemMain}>
                        {formatDate(invoice.date)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={classes.hint}>
                  By default we show only 100 last invoices. But we can select
                  contact to show all contact invoices
                </div>
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </div>
  )
}

export default injectStyles(styles)(XeroConnection)
